import { registerComponent, debounce, scrollLock } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "predictive-search",
  class PredictiveSearch extends WYATTElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.statusElement = this.querySelector(
        "[data-predictive-search-status]",
      );
      this.loadingText = this.statusElement.getAttribute("data-loading-text");
      this.minimumSearchLength = 3;
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search]",
      );
      this.allPredictiveSearchInstances =
        document.querySelectorAll("predictive-search");
      this.isOpen = false;
      this.mobileMediaQuery = window.matchMedia("(max-width: 1023px)");
      this.scrollLocked = false;
      this.abortController = new AbortController();
      this.searchTerm = "";
      this.onResizeHandler = debounce(
        this.updateResultsMaxHeight.bind(this),
        100,
      );

      if (this.input) {
        this.input.form.addEventListener("reset", this.onFormReset.bind(this));
        this.input.addEventListener("input", () => {
          this.toggleResetButton();
          if (this.getQuery().length < this.minimumSearchLength) {
            this.clearPredictiveResults();
          }
        });
        this.input.addEventListener(
          "input",
          debounce((event) => {
            this.onChange(event);
          }, 300).bind(this),
        );
      }

      this.setupEventListeners();
      window.addEventListener("resize", this.onResizeHandler);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.abortController.abort();
      window.removeEventListener("resize", this.onResizeHandler);
      this.unlockScroll();
    }

    lockScroll() {
      if (!this.mobileMediaQuery.matches || this.scrollLocked) return;
      scrollLock.enable();
      this.scrollLocked = true;
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      scrollLock.disable();
      this.scrollLocked = false;
    }

    toggleResetButton() {
      const resetIsHidden = this.resetButton.classList.contains("hidden");
      if (this.input.value.length > 0 && resetIsHidden) {
        this.resetButton.classList.remove("hidden");
      } else if (this.input.value.length === 0 && !resetIsHidden) {
        this.resetButton.classList.add("hidden");
      }
    }

    setupEventListeners() {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.addEventListener("keyup", this.onKeyup.bind(this));
      this.addEventListener("keydown", this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    clearPredictiveResults() {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.searchTerm = this.getQuery();
      this.close();
      this.removeAttribute("results");
      this.predictiveSearchResults.replaceChildren();
      this.statusElement.textContent = "";
      this.statusElement.setAttribute("aria-hidden", "true");
    }

    onChange() {
      this.toggleResetButton();
      const newSearchTerm = this.getQuery();

      if (newSearchTerm.length < this.minimumSearchLength) {
        this.clearPredictiveResults();
        return;
      }

      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector("#predictive-search-results-wrapper")?.remove();
      }

      this.updateSearchForTerm(newSearchTerm);
      this.searchTerm = newSearchTerm;
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (
        !this.getQuery().length ||
        this.querySelector('[aria-selected="true"] a')
      ) {
        event.preventDefault();
      }
    }

    shouldResetForm() {
      return !this.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      event.preventDefault();
      if (this.shouldResetForm()) {
        this.input.value = "";
        this.clearPredictiveResults();
        this.toggleResetButton();
        this.input.focus();
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();
      if (currentSearchTerm.length < this.minimumSearchLength) {
        this.clearPredictiveResults();
        return;
      }

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(currentSearchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (this.getQuery().length < this.minimumSearchLength) {
        this.clearPredictiveResults();
        return;
      }

      switch (event.code) {
        case "ArrowUp":
          event.preventDefault();
          this.switchOption("up");
          break;
        case "ArrowDown":
          event.preventDefault();
          this.switchOption("down");
          break;
        case "Enter":
          event.preventDefault();
          this.selectOption();
          break;
        case "Escape":
          this.close();
          this.input.focus();
          break;
        default:
      }
    }

    onKeydown(event) {
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
      }
    }

    updateSearchForTerm(newTerm) {
      const searchForTextElement = this.querySelector(
        "[data-predictive-search-search-for-text]",
      );
      if (searchForTextElement) searchForTextElement.innerText = newTerm;
    }

    switchOption(direction) {
      if (!this.getAttribute("open")) return;

      const moveUp = direction === "up";
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allVisibleElements = Array.from(
        this.querySelectorAll(
          "li, button.predictive-search__search-for-button",
        ),
      ).filter((element) => element.offsetParent !== null);
      if (!allVisibleElements.length) return;

      let activeElementIndex = 0;
      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;
      while (selectedElementIndex === -1 && i < allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
        i += 1;
      }

      if (!moveUp && selectedElement) {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex =
          selectedElementIndex <= 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;
      const activeElement = allVisibleElements[activeElementIndex];
      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) selectedElement.setAttribute("aria-selected", false);
      this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]',
      );
      if (selectedOption) selectedOption.click();
    }

    async getSearchResults(searchTerm) {
      if (
        searchTerm.length < this.minimumSearchLength ||
        searchTerm !== this.getQuery()
      )
        return;

      const queryKey = searchTerm.toLowerCase();
      this.setLiveRegionLoadingState();
      this.abortController.abort();
      this.abortController = new AbortController();
      const requestController = this.abortController;

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
        return;
      }

      try {
        const searchParams = new URLSearchParams({
          q: searchTerm,
          "resources[type]": "product,query,collection",
          "resources[limit]": "6",
          "resources[limit_scope]": "each",
          section_id: "predictive-search",
        });
        searchParams.set("resources[options][fields]", "variants.barcode");
        const response = await fetch(
          `${WYATT.routes.predictive_search_url}?${searchParams.toString()}`,
          { signal: requestController.signal },
        );

        if (requestController.signal.aborted || searchTerm !== this.getQuery())
          return;

        if (!response.ok) {
          this.close();
          throw new Error(String(response.status));
        }

        const text = await response.text();
        if (requestController.signal.aborted || searchTerm !== this.getQuery())
          return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search")?.innerHTML;

        if (resultsMarkup === undefined) {
          this.close();
          throw new Error("Predictive search section missing");
        }

        if (searchTerm !== this.searchTerm) return;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup, searchTerm);
      } catch (error) {
        if (
          requestController.signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === 20
        )
          return;
        if (searchTerm !== this.getQuery()) return;
        this.close();
        throw error;
      }
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute("aria-hidden", "false");
      this.statusElement.textContent = statusText;
      setTimeout(() => {
        this.statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    renderSearchResults(resultsMarkup, searchTerm) {
      if (
        this.getQuery().length < this.minimumSearchLength ||
        searchTerm !== this.getQuery()
      )
        return;
      this.predictiveSearchResults.replaceChildren();
      this.updateDOM.append(this.predictiveSearchResults, resultsMarkup);
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute("loading");
      this.setLiveRegionText(
        this.querySelector("[data-predictive-search-live-region-count-value]")
          .textContent,
      );
    }

    getResultsMaxHeight() {
      const headerBottom =
        document.querySelector(".page-header")?.getBoundingClientRect()
          .bottom || 0;
      return Math.max(window.innerHeight - headerBottom, 0);
    }

    updateResultsMaxHeight() {
      if (!this.isOpen) return;
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      if (this.mobileMediaQuery.matches) this.lockScroll();
      else this.unlockScroll();
    }

    open() {
      if (this.getQuery().length < this.minimumSearchLength) return;
      clearTimeout(this.closingTimeout);
      this.removeAttribute("closing");
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
      this.updateResultsMaxHeight();
      this.lockScroll();
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      this.unlockScroll();
      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
      }
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute("aria-selected", false);
      this.input.setAttribute("aria-activedescendant", "");
      this.removeAttribute("loading");
      this.input.setAttribute("aria-expanded", false);

      if (this.hasAttribute("open")) {
        this.removeAttribute("open");
        this.setAttribute("closing", true);
        this.closingTimeout = setTimeout(() => {
          this.removeAttribute("closing");
          this.predictiveSearchResults.removeAttribute("style");
        }, 300);
      } else {
        this.predictiveSearchResults.removeAttribute("style");
      }
    }
  },
);
